import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import { User, Mail, Lock, Globe, Camera, Save, Eye, EyeOff } from 'lucide-react';

const Settings = () => {
  const { t } = useTranslation();
  const { user, updateLanguage, token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    company: user?.company || '',
    language: user?.language || 'it'
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [profileImage, setProfileImage] = useState(user?.profile_image || null);
  const [previewImage, setPreviewImage] = useState(user?.profile_image || null);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.put('/auth/profile', {
        full_name: profileData.full_name,
        email: profileData.email,
        company: profileData.company
      });
      
      if (profileData.language !== user?.language) {
        await updateLanguage(profileData.language);
      }
      
      toast.success(t('common.success'));
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Le password non corrispondono');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.put('/auth/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      toast.success('Password aggiornata con successo');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('L\'immagine deve essere inferiore a 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setProfileImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!profileImage || typeof profileImage === 'string') return;
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', profileImage);
      
      await api.post('/auth/profile-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Immagine profilo aggiornata');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-fadeIn" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('nav.settings')}</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Gestisci il tuo profilo e le preferenze
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl w-full sm:w-auto flex flex-wrap">
          <TabsTrigger value="profile" className="rounded-lg flex-1 sm:flex-none" data-testid="tab-profile">
            <User size={16} className="mr-2 hidden sm:inline" />
            Profilo
          </TabsTrigger>
          <TabsTrigger value="security" className="rounded-lg flex-1 sm:flex-none" data-testid="tab-security">
            <Lock size={16} className="mr-2 hidden sm:inline" />
            Sicurezza
          </TabsTrigger>
          <TabsTrigger value="preferences" className="rounded-lg flex-1 sm:flex-none" data-testid="tab-preferences">
            <Globe size={16} className="mr-2 hidden sm:inline" />
            Preferenze
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid gap-6">
            {/* Profile Image */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Immagine Profilo</CardTitle>
                <CardDescription>
                  Carica una foto per personalizzare il tuo profilo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="relative">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                      {previewImage ? (
                        <img 
                          src={previewImage} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={40} className="text-primary" />
                      )}
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-0 right-0 w-8 h-8 sm:w-10 sm:h-10 bg-primary text-white rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
                      data-testid="change-image-btn"
                    >
                      <Camera size={16} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      data-testid="image-input"
                    />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="font-medium text-base sm:text-lg">{user?.full_name}</p>
                    <p className="text-muted-foreground text-sm">{user?.email}</p>
                    {profileImage && typeof profileImage !== 'string' && (
                      <Button 
                        onClick={handleImageUpload}
                        disabled={loading}
                        className="mt-4 bg-gradient-to-r from-violet-600 to-indigo-600"
                        size="sm"
                        data-testid="upload-image-btn"
                      >
                        <Save size={16} className="mr-2" />
                        Salva Immagine
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Profile Info */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Informazioni Personali</CardTitle>
                <CardDescription>
                  Aggiorna i tuoi dati personali
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">{t('auth.fullName')}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                        <Input
                          id="full_name"
                          value={profileData.full_name}
                          onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                          className="pl-10 h-11 sm:h-12 rounded-xl"
                          data-testid="input-fullname"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('auth.email')}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                        <Input
                          id="email"
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                          className="pl-10 h-11 sm:h-12 rounded-xl"
                          data-testid="input-email"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">{t('auth.company')}</Label>
                    <Input
                      id="company"
                      value={profileData.company}
                      onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                      className="h-11 sm:h-12 rounded-xl"
                      placeholder="Nome azienda (opzionale)"
                      data-testid="input-company"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full sm:w-auto bg-gradient-to-r from-violet-600 to-indigo-600 h-11 sm:h-12 rounded-xl px-8"
                    data-testid="save-profile-btn"
                  >
                    <Save size={18} className="mr-2" />
                    {loading ? t('common.loading') : 'Salva Modifiche'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Cambia Password</CardTitle>
              <CardDescription>
                Aggiorna la tua password per mantenere sicuro il tuo account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Password Attuale</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="current_password"
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      className="pl-10 pr-10 h-11 sm:h-12 rounded-xl"
                      required
                      data-testid="input-current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_password">Nuova Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="new_password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      className="pl-10 pr-10 h-11 sm:h-12 rounded-xl"
                      required
                      minLength={6}
                      data-testid="input-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Conferma Nuova Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      className="pl-10 h-11 sm:h-12 rounded-xl"
                      required
                      minLength={6}
                      data-testid="input-confirm-password"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full sm:w-auto bg-gradient-to-r from-violet-600 to-indigo-600 h-11 sm:h-12 rounded-xl px-8"
                  data-testid="change-password-btn"
                >
                  <Lock size={18} className="mr-2" />
                  {loading ? t('common.loading') : 'Aggiorna Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Preferenze</CardTitle>
              <CardDescription>
                Personalizza la tua esperienza
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6 max-w-md">
                <div className="space-y-2">
                  <Label>Lingua / Language</Label>
                  <Select 
                    value={profileData.language} 
                    onValueChange={(value) => {
                      setProfileData({ ...profileData, language: value });
                      updateLanguage(value);
                    }}
                  >
                    <SelectTrigger className="h-11 sm:h-12 rounded-xl" data-testid="language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="it">
                        <span className="flex items-center gap-2">
                          🇮🇹 Italiano
                        </span>
                      </SelectItem>
                      <SelectItem value="en">
                        <span className="flex items-center gap-2">
                          🇬🇧 English
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Seleziona la lingua dell'interfaccia
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
